import { CarPdfViewer } from "@app/components/PdfViewer";
import { useSearchParams } from "react-router-dom";
import { CarPdfDocument } from "@app/components/PdfDocument";
import { CarPdfPage, defaultClientPageSize } from "@app/components/PdfPage";
import { useUser } from "@app/pages/settings/useUser";
import { portfolioAnalysisPages } from "@app/const/route.const";
import { usePortfolioGroupInfo } from "@app/pages/assetAllocation/usePortfolioGroupInfo";
import { usePortfolioGroupVersionStatData } from "@app/pages/assetAllocation/usePortfolioGroupVersionStatData";
import { PortfolioAnalysisSummaryPdfChart } from "./PortfolioAnalysisSummaryPdfChart";
import { usePortfolioGroupVersionData } from "@app/pages/assetAllocation/usePortfolioGroupVersionData";
import { AssetClassLevel } from "@app/const";
import { CarPdfTypography } from "@app/components/PdfTypography";

const CHART_HEIGHT = 240;

interface ModuleSummaryReportProps {
  portfolioGroupId: string;
  moduleId: string;
  data1Id: string;
  data2Id: string;
  footer: string;
}

export const ModuleSummaryReport = ({
  portfolioGroupId,
  moduleId,
  data1Id,
  data2Id,
  footer,
}: ModuleSummaryReportProps) => {
  const groupInfo = usePortfolioGroupInfo({ portfolioGroupId });

  const { portfolioGroupVersionId, publishedVersionId, isSinglePortfolioMode } =
    groupInfo;

  const data = usePortfolioGroupVersionData({
    versionId: portfolioGroupVersionId,
    publishedVersionId,
    show: {
      benchmark: true,
      existing: true,
      draft: true,
      selected: false,
    },
    hideCash: true,
  });

  const user = useUser();

  const moduleName = data.getModuleNameById(moduleId);

  const title = `Summary - ${isSinglePortfolioMode ? groupInfo.title : moduleName}`;

  return (
    <CarPdfViewer
      isLoading={user.isLoading || groupInfo.isLoading || data.isLoading}
    >
      <CarPdfDocument title={title} author={user.userFullName}>
        {
          <CarPdfPage
            sectionTitle="Analyze"
            reportTitle={title}
            footer={footer}
          >
            <CarPdfTypography
              variant="h6"
              style={{ marginTop: 28, marginBottom: 8 }}
            >
              Level 1
            </CarPdfTypography>
            <PortfolioAnalysisSummaryPdfChart
              width={defaultClientPageSize.width}
              height={CHART_HEIGHT}
              moduleId={moduleId}
              tableData={data.tableData}
              level={AssetClassLevel.level1}
              data1Id={data1Id}
              data2Id={data2Id}
              isWhiteContext
            />
            <CarPdfTypography
              variant="h6"
              style={{ marginTop: 28, marginBottom: 8 }}
            >
              Level 2
            </CarPdfTypography>
            <PortfolioAnalysisSummaryPdfChart
              width={defaultClientPageSize.width}
              height={CHART_HEIGHT}
              moduleId={moduleId}
              tableData={data.tableData}
              level={AssetClassLevel.level2}
              data1Id={data1Id}
              data2Id={data2Id}
              isWhiteContext
            />
            <CarPdfTypography break variant="h6" style={{ marginBottom: 8 }}>
              Level 3
            </CarPdfTypography>
            <PortfolioAnalysisSummaryPdfChart
              width={defaultClientPageSize.width}
              height={220}
              moduleId={moduleId}
              tableData={data.tableData}
              level={AssetClassLevel.level3}
              data1Id={data1Id}
              data2Id={data2Id}
              isWhiteContext
            />
            <CarPdfTypography
              variant="h6"
              style={{ marginTop: 28, marginBottom: 8 }}
            >
              Level 4
            </CarPdfTypography>
            <PortfolioAnalysisSummaryPdfChart
              width={defaultClientPageSize.width}
              height={340}
              moduleId={moduleId}
              tableData={data.tableData}
              level={AssetClassLevel.level4}
              data1Id={data1Id}
              data2Id={data2Id}
              isWhiteContext
            />
          </CarPdfPage>
        }
      </CarPdfDocument>
    </CarPdfViewer>
  );
};
